import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { getMetricColor, StringValueYesNoChip } from "@/metrics/metric-display"
import type { Annotation } from "@/types"
import {
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@coval/ui"
import { Users } from "lucide-react"

import type { TargetCondition } from "@/types/metrics/contracts"
import { cn } from "@/utils/cn"

interface BinaryHumanFeedbackButtonProps {
  annotation: Annotation | null
  onValueChange: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
  ) => void
  onExplanationChange: (explanation: string | null) => void
  otherUserAnnotations?: Annotation[]
  targetCondition?: TargetCondition
}

export const BinaryHumanFeedbackButton = ({
  annotation,
  onValueChange,
  otherUserAnnotations,
  targetCondition,
}: BinaryHumanFeedbackButtonProps) => {
  const toggleOrUpdateValue = (value: string | null) => {
    if (annotation?.ground_truth_string_value === value) {
      onValueChange(null, null, null)
    } else {
      onValueChange(null, value, null)
    }
  }

  const currentValue = annotation?.ground_truth_string_value
  const hasOtherAnnotations =
    otherUserAnnotations && otherUserAnnotations.length > 0

  const getSelectedClass = (value: string): string => {
    const color = getMetricColor(value, targetCondition)
    if (color === "success") {
      return "bg-coval-success-surface text-coval-success-on-surface font-medium hover:bg-coval-success-surface hover:text-coval-success-on-surface"
    }
    if (color === "danger") {
      return "bg-coval-danger-surface text-coval-danger-on-surface font-medium hover:bg-coval-danger-surface hover:text-coval-danger-on-surface"
    }
    return "bg-surface-2 text-coval-text font-medium hover:bg-surface-2-hover hover:text-coval-text"
  }

  const hasVote = !!currentValue

  const renderVoteButton = (value: string, label: string) => {
    const isSelected = currentValue === value
    return (
      <Button
        variant="secondary"
        aria-pressed={isSelected}
        data-testid={`human-review-vote-${value.toLowerCase()}`}
        onClick={(e) => {
          e.stopPropagation()
          toggleOrUpdateValue(value)
        }}
        className={cn(
          "transition-all duration-200",
          isSelected
            ? getSelectedClass(value)
            : cn(
                "opacity-60",
                hasVote
                  ? "hover:bg-surface-1 hover:opacity-60"
                  : "hover:opacity-100",
              ),
        )}
      >
        {label}
      </Button>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>Correct value</Label>
      <p className="text-xs text-coval-text-muted">
        Choose the value this metric should have.
      </p>
      <div className="flex items-start justify-end gap-1.5">
        {renderVoteButton(ANNOTATION_VALUE.YES, "Yes")}
        {renderVoteButton(ANNOTATION_VALUE.NO, "No")}
        {renderVoteButton(ANNOTATION_VALUE.UNKNOWN, "N/A")}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              disabled={!hasOtherAnnotations}
              aria-label="View other user annotations"
              className="transition-all duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <Users className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" className="w-64 p-2">
            <div className="max-h-48 overflow-y-auto">
              {otherUserAnnotations?.map((otherAnnotation) => {
                const value = otherAnnotation.ground_truth_string_value

                return (
                  <div
                    key={otherAnnotation.id}
                    className="flex items-center justify-between py-1 px-1"
                  >
                    <div className="flex items-center gap-2">
                      <StringValueYesNoChip
                        value={value ?? ANNOTATION_VALUE.UNKNOWN}
                        targetCondition={undefined}
                      />
                      <span className="text-xs text-coval-text-muted">
                        {otherAnnotation.assignee}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
